/// <reference path="../../../../WebUI/typings/tsd.d.ts" />

import * as React from "react";
import * as ReactDOM from "react-dom";
import { ControllerBase } from "./ControllerBase";
import { DevPortalAppBase } from "../../DevPortalAppBase";
import * as Rest from "../../Generated/RestClient";
import * as Editor from '../../Views/Shared/IBotEditor';
import * as Views from "../Views/BotEditor";
import * as Dialogs from "../../Dialogs/MessageBox";
import * as Links from "../../Utils/Links";
import { ProgressDialog } from "../../Dialogs/ProgressDialog";
import { NavLinks, resourceStrings, BotImageControl } from "../../../../Intercom.ReactUI/Views/index";
import { IconFile } from "../Views/BotPage";
import { clientAppSettings } from "../../Models/ClientAppSettings";
import { BotVersionEditor } from "../../Views/BotVersionEditor";
import { log } from "../../../../Intercom.ReactUI/Utils/clacky";
import { BotAppSettingsProps } from "../Views/index";

export class BotEditController extends ControllerBase {
    private state: Views.BotEditorState;
    private initialBot: Rest.BotService;
    private component: React.Component<Views.BotEditorProps, Views.BotEditorState>;

    constructor(app: DevPortalAppBase, containerController?: ControllerBase,
        private botAppSettingsProps?: BotAppSettingsProps) {
        super(app);
        this.containerController = containerController;

        window.refreshBotMsaApp = (appId: string) => {
            log('/bots/new#closeMSAPage', 'Back from MSA dialog');
            this.state.bot.MsaAppId = appId;
            this.component.setState(this.state);
        };

    }

    public setComponent(editor: Views.BotEditor) {
        if (editor == null && this.hasUnsavedChanges()) {
            // probably switching tab with unsaved changes
            this.pendingUnsavedChanges = true;
        }
        this.component = editor;
        if (this.component) {
            this.component.setState(this.state);
            if (!this.initialBot) {
                this.initialBot = _.extend({}, this.state.bot);
            }
        }
    }

    private pendingUnsavedChanges = false;
    public hasUnsavedChanges(): boolean {
        if (!this.initialBot) {
            return false;
        }
        if (!this.component) {
            return this.pendingUnsavedChanges;
        }
        let updatedBot = <Rest.BotService>$.extend({}, this.initialBot);
        this.gatherFormValues(updatedBot);

        for (let field in updatedBot) {
            if (updatedBot.hasOwnProperty(field)) {
                let oldValue = this.initialBot[field];
                let newValue = updatedBot[field];

                if (newValue === '') {
                    if (_.isString(oldValue) && oldValue.length) {   // Note: if oldValue is null or undefined, that's not considered a diff.
                        return true;
                    }
                }
                else {
                    if ($.isArray(newValue)) {
                        if (newValue.length === 0) {
                            if ($.isArray(oldValue) && oldValue.length) {   // old oldValue is null or undefined, that's not considered a diff.
                                return true;
                            }
                        }
                        else {
                            if (!$.isArray(oldValue))
                                return true;
                            let newValueJSON = JSON.stringify(newValue);
                            let oldValueJSON = JSON.stringify(oldValue);
                            if (newValueJSON != oldValueJSON)
                                return true;
                        }
                    }
                    else {
                        // number, boolean, null, non-empty string
                        if (newValue != oldValue)
                            return true;
                    }
                }
            }
        }

        return false;
    }

    public isUnloadable(callback: (unloadable: boolean) => void): void {
        if (this.hasUnsavedChanges()) {
            Dialogs.MessageBox.ask("Unsaved changes will be lost. Are you sure?").done(() => {
                callback(true);
            }).fail(() => {
                callback(false);
            });
        }
        else {
            callback(true);
        }
    }

    public render(params: SimpleMvc.QueryParams): void {
        let botId = params["id"];

        super.render(params);

        this.state = Views.BotEditor.createNewState(null);

        this.renderPage(botId);
    }

    public updateBot(newBot: Rest.BotService): void {
        if (this.state && this.state.bot) {
            this.state.bot = newBot;
        }
    }

    private ensureCurrentVersionEndpoint(currVersion: string, endpoints: Rest.Endpoint[]): Rest.Endpoint[] {
        var result: Rest.Endpoint[] = [];

        var hasCurrentVersion = false;

        result = endpoints.map(function (endpoint) {
            if (endpoint.Version === currVersion) {
                hasCurrentVersion = true;
            }
            return endpoint;
        });

        if (!hasCurrentVersion) {
            result.push({ Url: '', Version: currVersion });
        }

        return result;
    }

    private onSystemTagsChange(tags: string[]) {
        if (this.state.bot) {
            this.state.bot.SystemHashtags = tags;
            this.component.setState(this.state);
        }
    }

    public getBotEditorProps(bot: Rest.BotService,
        botAppSettingsProps: BotAppSettingsProps,
        onSetEditor: (editor: Views.BotEditor) => void): Views.BotEditorProps {
        if (!this.state) {
            this.state = Views.BotEditor.createNewState(bot);
        } else {
            this.state.bot = bot;
        }
        return {
            initialBot: this.state.bot,
            inCreateMode: false,
            botAppSettingsProps: botAppSettingsProps,
            isAdmin: this.isAdminOrSupport(),
            onSetEditor: onSetEditor,
            onClickButton: _.debounce(x => this.onButtonClicked(x), 500, true),
            onDropIcon: (files: IconFile[]) => this.onDropIcon(files),
            isFeatureFlighted: this.app.isFeatureFlighted,
            onSystemTagsChange: _.bind(this.onSystemTagsChange, this),
            onSetBotAppSettings: x => this.setBotAppSettings(x)
        }
    };

    private renderPage(botId: string): void {
        if (botId) {
            this.setActiveJumboActiveLink(NavLinks.mybots);
            Rest.BotManagerClient.getById(botId).done(botSubscription => {
                if (!this.isLoaded())
                    return;
                this.state.bot = botSubscription.Bot;

                this.state.subscriptionKeys = {
                    primary: botSubscription.PrimarySubscriptionKey
                };

                //only allow users with v3 flight to upgrade
                if (this.app.isUserFeatureFlighted('v3')) {

                    //add the latest version to the array here so that the login in hasUnsavedChanges will detect the new info
                    this.state.bot.Endpoints = this.ensureCurrentVersionEndpoint('3.0', this.state.bot.Endpoints);
                }

                if (this.state.bot.MsaAppId) {
                    this.state.bot.MsaAppId = this.state.bot.MsaAppId.toLowerCase();
                }

                //make a copy of the bot for change comparisons - after we have made any adjustments
                this.initialBot = $.extend({}, this.state.bot);

                this.updateTitle(`${this.state.bot.Name} | edit details`);

                var props = this.getBotEditorProps(this.state.bot,  this.botAppSettingsProps, x => { });
                var element = React.createElement(Views.BotEditor, props);
                this.component = ReactDOM.render<Views.BotEditorProps, Views.BotEditor>(element, this.pageContent, () => this.setupEventHandlers());

                // When pressing browser back to switch between tabs of this page React reuses existing component.
                // So we have to update the state of the existing component.
                this.component.setState(this.state);
            }).fail((jqXHR: JQueryXHR, textStatus, errorThrown) => {
                this.handleHttpResponseError(jqXHR);
            });
        } else {
            this.setActiveJumboActiveLink(NavLinks.mybots);

            var email = this.app.signedInUser ? this.app.signedInUser.EmailAddress : '';
            let defaultIconUrl = clientAppSettings.defaultIconUrl;
            
            // everyone gets new bots except people in the v1 flight
            var newBotVersion = this.app.isUserFeatureFlighted('v1') ? '1.0' : '3.0';

            let endpoints: Rest.Endpoint[] = [
                { Url: '', Version: newBotVersion }
            ];

            let newBot = <Rest.BotService><Rest._BotService>{
                Name: '',
                Description: '',
                PublisherEmail: email,
                Endpoint: '',
                Publisher: '',
                Website: '',
                SystemHashtags: [],
                OwnerIds: [email],
                Languages: ['en'],
                Listed: false,
                IconUrl: defaultIconUrl,
                DefaultIconColorHash: `${new Date().getTime()}`,
                DeveloperAppInsightKey: '',
                DeveloperAppInsightsAPIKey: '',
                DeveloperAppInsightsApplicationId: '',
                DeveloperBotPrivacyUrl: '',
                Id: '',
                MsaAppId: '',
                ListedMarkets: this.app.isFeatureFlighted('markets') ? ['*'] : null,
                Version: newBotVersion,
                Endpoints: endpoints
            };
            newBot.IconUrl = `${defaultIconUrl.substr(0, defaultIconUrl.lastIndexOf('/'))}/${BotImageControl.hashDefaultIconFileName(newBot)}`;
            this.initialBot = $.extend({}, newBot);
            this.updateTitle(`Register | my bots`);

            var props: Views.BotEditorProps = {
                initialBot: newBot,
                inCreateMode: true,
                botAppSettingsProps: this.botAppSettingsProps,
                onDropIcon: (files: IconFile[]) => this.onDropIcon(files),
                onClickButton: _.debounce(x => this.onButtonClicked(x), 500, true),
                onSetEditor: x => { },
                isFeatureFlighted: this.app.isFeatureFlighted,
                isAdmin: this.isAdminOrSupport(),
                onSystemTagsChange: _.bind(this.onSystemTagsChange, this),
                onSetBotAppSettings: x => this.setBotAppSettings(x)
            };

            var element = React.createElement(Views.BotEditor, props);
            this.component = ReactDOM.render<Views.BotEditorProps, Views.BotEditor>(element, this.pageContent, () => this.setupEventHandlers());
            this.state.bot = props.initialBot;
            this.component.setState(this.state);
        }
    }

    private onDropIcon(files: IconFile[]) {
        if (files && files.length) {
            var file = files[0];
            var reader = new FileReader();
            reader.onload = (ev: Event) => {
                Rest.BotManagerClient.setBotIcon(ev.target['result'], this.state.bot ? this.state.bot.Id : null).done(url => {
                    var nextState = _.extend({}, this.state);
                    nextState.bot.IconUrl = url;
                    this.component.setState(nextState);
                }).fail((xhr) => {
                    this.handleHttpResponseError(xhr);
                });
            };

            reader.readAsDataURL(file);
            // create a clone of state so that incremental render will trigger
            this.component.setState(_.extend({}, this.state, { iconFile: file }));
        }
    }

    private setupEventHandlers(): void {
    }

    private onButtonClicked(ev: Event) {
        let $button = $(ev.target);
        if ($button.hasClass('register-button')) {
            this.onRegisterClicked();
        } else if ($button.hasClass('delete-button')) {
            this.onDeleteClicked();
        } else if ($button.hasClass('save-button')) {
            this.onSaveClicked();
        } else if ($button.hasClass('get-agent-appId')) {
            this.onGetAppIdClicked(ev);
        } else if ($button.hasClass('regenerate-link')) {
            this.onRegenerateKeyClicked(ev)
        } else if ($button.hasClass('cancel-button')) {
            this.onCancel();
        }

    }
    private onCancel(): void {
        if (this.component.props.inCreateMode)
            this.app.navigate("/bots/provision");
        else
            this.app.navigate(Links.botViewLink(this.state.bot));
    }

    private isValidEmail(email: string): boolean {
        return email.match("^[^@]+@[^@]+\.[^@]+$") !== null;
    }

    private validateEmailList(s: string): string {
        let error = null;
        let list = s.split(/[;,]+/);
        if (s.match(/@/g).length > 1 && list.length < 2)
            return 'Incorrect separator for the owner email';
        for (let email of list) {
            if (!this.isValidEmail(email)) {
                return 'One or more email addresses are not valid.';
            }
        }
        return error;
    }

    private isValidNameFieldLength(value: string): boolean {
        return (value.length >= Editor.MIN_NAME_LEN && value.length <= Editor.MAX_NAME_LEN);
    }

    private gatherFormValues(bot: Rest.BotService): Editor.ErrorDict {
        var errors = {} as Editor.ErrorDict;
        var requiredFieldError = "* Required field";
        var displayNameLengthError = "* Display name must be between " + Editor.MIN_NAME_LEN + " and " + Editor.MAX_NAME_LEN + " characters"; 
        var botHandleLengthError = "* Bot handle must be between " + Editor.MIN_NAME_LEN + " and " + Editor.MAX_NAME_LEN + " characters"; 

        var $page = $(this.pageContent);

        //helper to get a string of a strongly typed name
        var x = {} as Rest.BotService;

        function name(value: any) {
            for (var key in x) {
                delete x[key];
                return key;
            }
            return null;
        }

        if (this.state.bot.Endpoints.length > 1) {
            bot.Version = $page.find('[name=input-Version]:radio').filter(':checked').val();
        } else {
            bot.Version = this.state.bot.Version;
        }

        var endpoints: Rest.Endpoint[] = [];
        this.state.bot.Endpoints.map(function (endpoint, index) {
            endpoint.Url = $page.find('#' + BotVersionEditor.endpointId(index)).val();
            endpoints.push(endpoint);

            if (endpoint.Version === bot.Version) {
                bot.Endpoint = endpoint.Url;
            }
        });

        bot.Endpoints = endpoints;

        // keys of values to copy
        var copy = {
            DeveloperAppInsightKey: null,
            DeveloperAppInsightsAPIKey: null,
            DeveloperAppInsightsApplicationId: null,
            MsaAppId: null,
            MsaAppType: null,
            MsaAppMSIResourceId: null,
            MsaAppTenantId: null,
            Description: null
        } as Rest.BotService;

        for (var key in copy) {
            let value = $page.find("#input-" + key).val();
            if (!_.isUndefined(value)) {
                bot[key] = value;
            }
        }

        //keys of required values
        var required = {
            Name: null,
            Id: null
        } as Rest.BotService;

        //not required for v1
        if (bot.Version === '3.0') {
            required.MsaAppId = null;
        }

        for (var key in required) {
            bot[key] = $page.find("#input-" + key).val();
            if (!bot[key]) {
                errors[key] = requiredFieldError;
            }
            // if the bot is new 
            if (typeof bot.LastSavedDate === "undefined") {
                if (key === 'Name' && !this.isValidNameFieldLength(bot[key])) 
                    errors[key] = displayNameLengthError;
                if (key === 'Id' && !this.isValidNameFieldLength(bot[key])) 
                    errors[key] = botHandleLengthError;
            }
        }

        bot.IconUrl = $page.find("#agent-icon-url").val();
        bot.DefaultIconColorHash = $page.find("#agent-icon-default-hash").val();

        let $sysTags = $page.find('#agent-systemHashtags');
        if ($sysTags.length) {
            bot.SystemHashtags = $sysTags.val().split(',').filter(t => t.length);
        }
        var ownerIds: string = $page.find("#input-OwnerIds").val();
        if (ownerIds) {
            var ownerIdsError = this.validateEmailList(ownerIds);
            if (ownerIdsError)
                errors[name(x.OwnerIds = null)] = ownerIdsError;
            else
                bot.OwnerIds = ownerIds.split(/[;,]+/).map(email => email.trim());
        }

        return errors;
    }

    private onRegenerateKeyClicked(ev: Event): void {
        if (this.state.bot && this.state.bot.Id) {
            ev.preventDefault();
            var isPrimary = $(ev.target).hasClass('primary');
            Dialogs.MessageBox.ask("Are you sure you want to regenerate this key?").done(() => {
                this.regenerateKey(isPrimary);
            });
        }
    }

    private regenerateKey(isPrimary: boolean) {
        var progressDialog = ProgressDialog.show("Regenerating secret...");
        Rest.BotManagerClient.regenerateSubscriptionKey(this.state.bot.Id, isPrimary).done(botSubscription => {
            if (this.isLoaded()) {
                progressDialog.closeDialog();

                var newState: Views.BotEditorState = {
                    subscriptionKeys: {
                        primary: botSubscription.PrimarySubscriptionKey
                    }
                };

                this.component.setState(newState);
            }
        }).fail(xhr => {
            this.handleHttpResponseError(xhr);
        });
    }

    private onRegisterClicked(): void {
        var bot = <Rest.BotService>{};
        if (!this.component.state.termAcknowledged) {
            $('#agreeToTerms').addClass('must-check');
            Dialogs.MessageBox.show(resourceStrings.errors.agreementNotChecked);
            return;
        }

        var errors = this.gatherFormValues(bot);
        if (!$.isEmptyObject(errors)) {
            this.state.errors = errors;
        } else {
            this.state.errors = {};
        }

        var progressDialog = ProgressDialog.show("Saving...");

        Rest.BotManagerClient.create(bot).done(createdBot => {
            log('/bots/new#created', 'bot registered');
            if (!this.isLoaded())
                return;
            if (!this.redirectIfNeeded()) {
                this.state.bot = createdBot;
                this.initialBot = $.extend({}, createdBot);

                // The DevPortalLib service may add system hashtags that don't exist in the bot registration form.
                // This causes client side logic to flag the new bot as having unsaved changes. Solution here is
                // to add the new system hashtags to the form hasUnsavedChanges() doesn't erroneously flag it.
                const $page = $(this.pageContent);
                const $sysTags = $page.find('#agent-systemHashtags');
                if ($sysTags) {
                    const val = $sysTags.val() || '';
                    let tags = val
                        .split(',')
                        .concat(this.initialBot.SystemHashtags)
                        .map(item => item.trim());
                    tags = tags.filter((item, pos) => item.length && tags.indexOf(item) == pos)
                    $sysTags.val(tags.join(','));
                }

                progressDialog.closeDialog();
                Dialogs.MessageBox.show("Bot created").done(() => {
                    this.app.navigate('/bots', { id: createdBot.Id });
                });
            }
        }).fail(xhr => {
            progressDialog.closeDialog();
            this.handleHttpResponseError(xhr);
        });
    }

    protected handleHttpResponseError(xhr: JQueryXHR): JQueryPromise<any> {
        let errors: Rest.FieldValidationError[] = null;

        if (xhr && xhr.responseText) {
            try {
                var errorObj: Rest.FormValidationErrors = $.parseJSON(xhr.responseText);
                errors = errorObj.Errors;
            }
            catch (ex) {
            }
        }
        if (!errors || !errors.length) {
            return super.handleHttpResponseError(xhr);
        }

        // map errors array to dictionary
        var errorMap: { [key: string]: string } = {};
        errors.forEach(error => {
            let fieldId = error.Field;
            if (!errorMap[fieldId]) {
                errorMap[fieldId] = error.Message;
            }
        });

        this.state.errors = _.extend(errorMap, this.state.errors || {});

        this.component.setState(this.state);
        return Dialogs.MessageBox.show("Please correct the errors and try again.").done(() => {
            let $field = $('.error-info:visible').closest('.detail-row');
            if ($field.length) {
                $field[0].scrollIntoView();
            }
        });
    }

    private onDeleteClicked(): void {
        if (this.state.bot && this.state.bot.Id) {
            var options: Dialogs.IMessageBoxOptions = {
                okButtonLabel: resourceStrings.deleteBot,
                cancelButtonLabel: resourceStrings.cancel,
                className: 'danger'
            };
            if (this.isAdminOrSupport() && $.inArray(this.app.signedInUser.EmailAddress, this.state.bot.OwnerIds) == -1) {
                if (!confirm("You are not the owner of " + this.state.bot.Id + ". Are you sure that you want to delete " + this.state.bot.Id +"?")) {
                    return;
                } 
            }
            var mb = new Dialogs.MessageBox(options);
            mb.header = "You are about to delete this bot";
            mb.message = "This action will erase all data associated with this bot and cannot be undone. Are you sure?";
            mb.showDialog().done(() => {
                this.performDeleteBot();
            });
        }
    }

    private performDeleteBot(): void {
        var progressDialog = ProgressDialog.show("Deleting bot...");
        Rest.BotManagerClient.delete(this.state.bot.Id).done(() => {
            if (this.isLoaded()) {
                progressDialog.closeDialog();
                Dialogs.MessageBox.show("Bot deleted").done(() => {
                    this.app.navigate("/bots");
                });
            }
        }).fail(xhr => {
            progressDialog.closeDialog();
            this.handleHttpResponseError(xhr);
        });
    }

    private onGetAppIdClicked(ev: Event) {
        var errors = this.gatherFormValues(this.state.bot);

        //only need to validate bot name
        if ("Name" in errors) {
            ev.preventDefault();

            var newErrors: Editor.ErrorDict = { "Name": errors["Name"] };

            $("#agent-details-card")[0].scrollIntoView();

            this.state.errors = newErrors;
            this.component.setState(this.state);
            Dialogs.MessageBox.show("Please enter a bot name.");
        }
    }

    private onSaveClicked(): void {
        if (this.state.bot && this.state.bot.Id) {
            var errors = this.gatherFormValues(this.state.bot);
            if (!$.isEmptyObject(errors)) {
                this.state.errors = errors;
            } else {
                this.state.errors = {};
            }
            this.state.isBusy = true;
            this.component.setState(this.state);

            Rest.BotManagerClient.update(this.state.bot, false).done(bot => {
                this.initialBot = $.extend({}, bot);
                this.pendingUnsavedChanges = false;
                if (this.isLoaded()) {
                    this.state.bot = bot;
                }
            }).fail(xhr => {
                this.handleHttpResponseError(xhr);
            }).always(() => {
                if (this.isLoaded()) {
                    this.state.isBusy = false;
                    this.component.setState(this.state);
                }
            });
        }
    }

}



// WEBPACK FOOTER //
// ./Client/v4/Controllers/BotEditController.ts